import { Camera, CameraResultType, CameraSource } from "@capacitor/camera";
import { Capacitor } from "@capacitor/core";
import { Filesystem } from "@capacitor/filesystem";

export async function getImageDimensions(imageUrl: string) {
  const image = new Image();

  image.src = imageUrl;

  await new Promise((resolve, reject) => {
    image.onload = resolve;
    image.onerror = reject;
  });

  return { width: image.width, height: image.height };
}

export async function getImagesDetails(imageUrls: string[]) {
  return Promise.all(
    imageUrls.map(async (imageUrl) => {
      const [blob, { width, height }] = await Promise.all([
        fetch(imageUrl).then((r) => r.blob()),
        getImageDimensions(imageUrl),
      ]);

      return { blob, width, height };
    })
  );
}

export async function getImageBase64(params: { path?: string; webPath?: string }) {
  if (Capacitor.isNativePlatform()) {
    if (params.path === undefined) {
      console.error("path should be defined in native platform");
      return;
    }

    return Capacitor.getPlatform() === "ios"
      ? getIOSImageBase64(params.path)
      : getWebImageBase64(Capacitor.convertFileSrc(params.path));
  }

  if (params.webPath === undefined) {
    console.error("webPath should be defined in web platform");
    return;
  }

  return getWebImageBase64(params.webPath);
}

async function getWebImageBase64(webPath: string) {
  const fileName = webPath.split("/").pop() ?? "image.jpg";
  const format = getImageFormat(fileName, "image/jpeg");
  const image = await loadImage(webPath);

  return getCompressedBase64FromImageElement({
    element: image,
    fileName: fileName,
    format: format,
  });
}

/**
 * Since iOS won't allow us to fetch the canvas data url when fetching a photo from external url,
 * we'll fetch read the photo from the filesystem (as base64) and then draw it on a canvas.
 */
async function getIOSImageBase64(imagePath: string) {
  const absolutePath = imagePath.startsWith("file://") ? imagePath : `file:///private${imagePath}`;

  const data = await Filesystem.readFile({ path: absolutePath });
  const fileName = imagePath.split("/").pop() ?? "image.jpg";
  const format = getImageFormat(fileName, "image/jpeg");
  const image = await loadImage(`data:${format};base64,${data.data}`);

  return getCompressedBase64FromImageElement({
    element: image,
    fileName: fileName,
    format: format,
  });
}

function getCompressedBase64FromImageElement(params: {
  element: HTMLImageElement;
  format: string;
  fileName: string;
}) {
  const canvas = drawImageInCanvas(params.element);

  return canvas.toDataURL("image/jpeg", 0.9);
}

function getImageFormat<T>(imagePath: string, fallback: T) {
  const lowerImagePath = imagePath.toLowerCase();

  if (lowerImagePath.endsWith(".jpg")) {
    return "image/jpeg";
  }

  if (lowerImagePath.endsWith(".png")) {
    return "image/png";
  }

  if (lowerImagePath.endsWith(".gif")) {
    return "image/gif";
  }

  return fallback;
}

export async function mergeImages(imageUrls: string[], options?: Partial<{ gap: number }>) {
  if (imageUrls.length === 1) {
    return imageUrls[0];
  }

  const gap = options?.gap ?? 24;
  const imageEls = await Promise.all(imageUrls.map(loadImage));
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");

  if (ctx === null) {
    throw new Error("ctx should be defined");
  }

  canvas.style.backgroundColor = "white";
  canvas.width = imageEls.reduce((acc, el) => Math.max(acc, el.width), 0);
  canvas.height = imageEls.reduce((acc, el) => acc + el.height, 0) + gap * (imageUrls.length - 1);

  ctx.fillStyle = "white";
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  let currentHeight = 0;

  for (const image of imageEls) {
    ctx.drawImage(image, 0, currentHeight);
    currentHeight += image.height + gap;
  }

  return canvas.toDataURL("image/jpeg");
}

async function loadImage(imagePath: string) {
  const fromFetch = await fetch(imagePath, { referrerPolicy: "same-origin" })
    .then((response) => response.blob())
    .then((blob) => URL.createObjectURL(blob));

  const image = new Image();

  image.src = fromFetch;

  return new Promise<HTMLImageElement>((resolve, reject) => {
    image.onload = () => resolve(image);
    image.onerror = reject;
  });
}

function drawImageInCanvas(image: HTMLImageElement) {
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");

  if (ctx === null) {
    throw new Error("ctx should be defined");
  }

  const size = getImageSizeWithMaxValues(
    { width: image.width, height: image.height },
    { width: 1440, height: 1440 }
  );

  canvas.width = size.width;
  canvas.height = size.height;

  // Important to keep in mind that Safari can't handle canvas elements
  // having more than 16777216 pixels. So make sure width * height <= 16777216
  ctx.drawImage(image, 0, 0, size.width, size.height);

  return canvas;
}

function getImageSizeWithMaxValues(
  original: { width: number; height: number },
  max: { width: number; height: number }
) {
  const ratio = Math.min(max.width / original.width, max.height / original.height);

  return { width: original.width * ratio, height: original.height * ratio };
}

export function getPhotoFromCamera() {
  return Camera.getPhoto({
    width: 1440,
    height: 1440,
    resultType: CameraResultType.Uri,
    source: CameraSource.Camera,
  });
}
